import { GatewayCreateUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/gateway-create-user.model';
import { GatewayUpdateUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/gateway-update-user.model';
import { GetUsersPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/get-users-paginated.model';
import { GatewayUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/outputs/gateway-user.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  USER_FILTERS_LSK,
  USER_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  USER_LIST_DEFAULT_OPTIONS,
  USER_LIST_FILTER_NAME_LIST,
  USER_LIST_SORT_FIELD,
  USER_LIST_SORT_ORDER,
  UserListOptionsModel,
} from './user-list/user-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const UserContext = createContext<GatewayUserModel | null>(null);

export const useUserContext = (): GatewayUserModel => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUserContext must be used within UserContext.Provider');
  }

  return context;
};

interface UserContextProviderProps {
  data: GatewayUserModel;
  children: React.ReactNode;
}

export const UserContextProvider = ({
  data,
  children,
}: UserContextProviderProps): React.JSX.Element => {
  return <UserContext.Provider value={data}>{children}</UserContext.Provider>;
};

// /////////////////////////////////////////////////////////////////////////////

export interface UserListContextProps {
  getPaginatedQueryParameters: GetUsersPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetUsersPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
  columnOptions: UserListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<UserListOptionsModel>>;
}

export const UserListContext = createContext<UserListContextProps | null>(null);

export const useUserListContext = (): UserListContextProps => {
  const context = useContext(UserListContext);

  if (!context) {
    throw new Error(
      'useUserListContext must be used within UserListContext.Provider',
    );
  }

  return context;
};

interface UserListContextProviderProps {
  children: React.ReactNode;
}

export const UserListContextProvider = ({
  children,
}: UserListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetUsersPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        customerId: [],
        roleId: [],
        textToSearch: '',
        // Sortable fields
        sortField: USER_LIST_SORT_FIELD,
        sortOrder: USER_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...USER_LIST_FILTER_NAME_LIST,
      ],
      USER_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<UserListOptionsModel>(
      USER_LIST_DEFAULT_OPTIONS,
      USER_LIST_OPTIONS_LSK,
    );

  const data = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      // setGetPaginatedLimitList,
      columnOptions,
      setColumnOptions,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    // setGetPaginatedLimitList,
    columnOptions,
    setColumnOptions,
  ]);

  return (
    <UserListContext.Provider value={data}>{children}</UserListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterUserListContext =
  createContext<DeepPartialSkipArrayKey<GetUsersPaginatedModel> | null>(null);

export const useFilterUserListContext =
  (): DeepPartialSkipArrayKey<GetUsersPaginatedModel> => {
    const context = useContext(FilterUserListContext);

    if (!context) {
      throw new Error(
        'useFilterUserListContext must be used within FilterUserListContext.Provider',
      );
    }

    return context;
  };

interface FilterUserListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetUsersPaginatedModel>;
  children: React.ReactNode;
}

export const FilterUserListContextProvider = ({
  data,
  children,
}: FilterUserListContextProviderProps): React.JSX.Element => {
  return (
    <FilterUserListContext.Provider value={data}>
      {children}
    </FilterUserListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchUserListContext =
  createContext<DeepPartialSkipArrayKey<GetUsersPaginatedModel> | null>(null);

export const useFilterSearchUserListContext =
  (): DeepPartialSkipArrayKey<GetUsersPaginatedModel> => {
    const context = useContext(FilterSearchUserListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchUserListContext must be used within FilterSearchUserListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchUserListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetUsersPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchUserListContextProvider = ({
  data,
  children,
}: FilterSearchUserListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchUserListContext.Provider value={data}>
      {children}
    </FilterSearchUserListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const CreateUserContext =
  createContext<DeepPartialSkipArrayKey<GatewayCreateUserModel> | null>(null);

export const useCreateUserContext =
  (): DeepPartialSkipArrayKey<GatewayCreateUserModel> => {
    const context = useContext(CreateUserContext);

    if (!context) {
      throw new Error(
        'useCreateUserContext must be used within CreateUserContext.Provider',
      );
    }

    return context;
  };

interface CreateUserContextProviderProps {
  data: DeepPartialSkipArrayKey<GatewayCreateUserModel>;
  children: React.ReactNode;
}

export const CreateUserContextProvider = ({
  data,
  children,
}: CreateUserContextProviderProps): React.JSX.Element => {
  return (
    <CreateUserContext.Provider value={data}>
      {children}
    </CreateUserContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UpdateUserContext = createContext<{
  userId: GatewayUserModel['id'];
  user: GatewayUpdateUserModel;
} | null>(null);

export const useUpdateUserContext = (): {
  userId: GatewayUserModel['id'];
  user: GatewayUpdateUserModel;
} => {
  const context = useContext(UpdateUserContext);

  if (!context) {
    throw new Error(
      'useUpdateUserContext must be used within UpdateUserContext.Provider',
    );
  }

  return context;
};

interface UpdateUserContextProviderProps {
  data: {
    userId: GatewayUserModel['id'];
    user: GatewayUpdateUserModel;
  };
  children: React.ReactNode;
}

export const UpdateUserContextProvider = ({
  data,
  children,
}: UpdateUserContextProviderProps): React.JSX.Element => {
  return (
    <UpdateUserContext.Provider value={data}>
      {children}
    </UpdateUserContext.Provider>
  );
};
